import type { OfferDto } from "@boomap/contracts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AddButton } from "./components/add-button.tsx";
import { BasemapToggle } from "./components/basemap-toggle.tsx";
import { DeclareSheet } from "./components/declare-sheet.tsx";
import { LocateButton } from "./components/locate-button.tsx";
import { MapInfo } from "./components/map-info.tsx";
import { PlacementBar } from "./components/placement-bar.tsx";
import { SearchBox } from "./components/search-box.tsx";
import { SpotSheet } from "./components/spot-sheet.tsx";
import { LocaleToggle } from "./components/locale-toggle.tsx";
import { useI18n } from "./i18n/index.ts";
import { apiErrorMessage } from "./lib/api-error-message.ts";
import {
  ApiRequestError,
  declareSpot,
  deleteSpot,
  reportSpot,
  takeDownSpot,
} from "./lib/api-client.ts";
import { focusOnPlace, type GeoPoint, type MapFocus } from "./lib/types.ts";
import { useBasemap } from "./lib/use-basemap.ts";
import { useSpots } from "./lib/use-spots.ts";
import { MapView, type MapViewHandle } from "./map/map-view.tsx";

/**
 * La création se fait en deux temps : viser, puis décrire. Séparer les deux
 * garde la carte visible pendant qu'on vise, et évite la feuille modale tant
 * que rien n'est décidé.
 */
type Panel =
  | { readonly kind: "none" }
  | { readonly kind: "placing" }
  | { readonly kind: "declare"; readonly location: GeoPoint }
  | { readonly kind: "spot"; readonly spotId: string };

export const App = (): React.ReactElement => {
  const { t } = useI18n();
  const spots = useSpots();
  const { basemap, setBasemap } = useBasemap();
  const [panel, setPanel] = useState<Panel>({ kind: "none" });
  const [focus, setFocus] = useState<MapFocus | null>(null);
  const [busy, setBusy] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const mapRef = useRef<MapViewHandle>(null);

  const selectedSpot = useMemo(
    () =>
      panel.kind === "spot" ? (spots.spots.find((spot) => spot.id === panel.spotId) ?? null) : null,
    [panel, spots.spots],
  );

  const closePanel = useCallback(() => {
    setPanel({ kind: "none" });
    setPanelError(null);
  }, []);

  // Échap referme le mode placement ou la feuille ouverte : sur ordinateur,
  // c'est le réflexe attendu pour annuler, et cela évite d'avoir à viser
  // « Annuler » à la souris.
  useEffect(() => {
    if (panel.kind === "none") {
      return undefined;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        closePanel();
      }
    };

    globalThis.addEventListener("keydown", onKeyDown);

    return () => globalThis.removeEventListener("keydown", onKeyDown);
  }, [panel.kind, closePanel]);

  const confirmPlacement = useCallback(() => {
    const target = mapRef.current?.placementTarget();

    if (target !== undefined) {
      setPanel({ kind: "declare", location: target });
    }
  }, []);

  const submitDeclaration = useCallback(
    async (location: GeoPoint, input: { offer: OfferDto; note: string | null }) => {
      setBusy(true);
      setPanelError(null);

      try {
        const created = await declareSpot({
          latitude: location.latitude,
          longitude: location.longitude,
          treats: input.offer.treats,
          decorated: input.offer.decorated,
          note: input.note,
        });

        spots.upsert(created);
        setPanel({ kind: "spot", spotId: created.id });
        setToast(t.toast.added);
      } catch (error) {
        if (error instanceof ApiRequestError && error.code === "spot_already_declared_nearby") {
          const existingId = error.details["existingSpotId"];

          if (typeof existingId === "string") {
            setPanel({ kind: "spot", spotId: existingId });
            setToast(t.toast.alreadyThere);
            return;
          }
        }

        setPanelError(apiErrorMessage(error, t, t.errors.network));
      } finally {
        setBusy(false);
      }
    },
    [spots, t],
  );

  const submitReport = useCallback(
    async (spotId: string) => {
      setBusy(true);
      setPanelError(null);

      try {
        const result = await reportSpot(spotId);

        if (result.alreadyReported) {
          setToast(t.toast.alreadyReported);
        } else if (result.hidden) {
          spots.remove(spotId);
          closePanel();
          setToast(t.toast.removed);
        } else {
          setToast(t.toast.reported);
          spots.refresh();
        }
      } catch (error) {
        setPanelError(apiErrorMessage(error, t, t.errors.network));
      } finally {
        setBusy(false);
      }
    },
    [closePanel, spots, t],
  );

  const submitRetraction = useCallback(
    async (spotId: string) => {
      setBusy(true);
      setPanelError(null);

      try {
        await deleteSpot(spotId);
        spots.remove(spotId);
        closePanel();
        setToast(t.toast.retracted);
      } catch (error) {
        setPanelError(apiErrorMessage(error, t, t.errors.network));
      } finally {
        setBusy(false);
      }
    },
    [closePanel, spots, t],
  );

  const submitTakeDown = useCallback(
    async (spotId: string) => {
      setBusy(true);
      setPanelError(null);

      try {
        await takeDownSpot(spotId);
        spots.remove(spotId);
        closePanel();
        setToast(t.toast.takenDown);
      } catch (error) {
        setPanelError(apiErrorMessage(error, t, t.errors.network));
      } finally {
        setBusy(false);
      }
    },
    [closePanel, spots, t],
  );

  return (
    <div className="shell">
      <MapView
        ref={mapRef}
        basemap={basemap}
        spots={spots.spots}
        pendingLocation={panel.kind === "declare" ? panel.location : null}
        placing={panel.kind === "placing"}
        mapLabel={t.app.mapLabel}
        focus={focus}
        onAreaChange={spots.setArea}
        onSpotClick={(spotId) => {
          setPanelError(null);
          setPanel({ kind: "spot", spotId });
        }}
      />

      <header className="topbar">
        <div className="topbar__brand">
          <span className="topbar__logo" aria-hidden="true">
            🎃
          </span>
          <div>
            <h1>{t.app.name}</h1>
            <p>{t.app.tagline}</p>
          </div>
        </div>

        {/* La localisation est collée à la recherche : les deux répondent à la
            même question — « amène-moi quelque part ». */}
        <div className="searchbar">
          <LocateButton
            onLocated={(point) => setFocus({ kind: "point", ...point, zoom: 17 })}
            onError={setToast}
          />
          <SearchBox onSelect={(place) => setFocus(focusOnPlace(place))} />
        </div>

        {/* L'action principale prend la place qu'occupait la légende. */}
        <AddButton
          hidden={panel.kind !== "none"}
          onClick={() => {
            setPanelError(null);
            setPanel({ kind: "placing" });
          }}
        />
      </header>

      <div className="floating">
        <LocaleToggle />
        <BasemapToggle basemap={basemap} onChange={setBasemap} />
      </div>

      {/* Retiré tant qu'un panneau est ouvert : sur mobile la feuille occupe
          toute la largeur et le bouton lui passait devant. Et poser un repère
          n'est pas le moment de consulter la légende. Contrairement au bouton
          d'ajout, celui-ci est positionné en absolu : le démonter ne décale
          rien. */}
      {panel.kind === "none" && <MapInfo />}

      {spots.zoomedOut && <output className="banner">{t.banner.zoomedOut}</output>}

      {spots.truncated && <output className="banner">{t.banner.truncated}</output>}

      {spots.error !== null && (
        <p className="banner banner--error" role="alert">
          {spots.error}
        </p>
      )}

      {/* Le toast vit dans la même pile que la feuille : empilés, ils ne se
          recouvrent jamais, quelle que soit la hauteur de la feuille. */}
      <div className="panel">
        {toast !== null && (
          <output className="toast" onAnimationEnd={() => setToast(null)}>
            {toast}
          </output>
        )}

        {panel.kind === "placing" && (
          <PlacementBar onCancel={closePanel} onConfirm={confirmPlacement} />
        )}

        {panel.kind === "declare" && (
          <DeclareSheet
            location={panel.location}
            busy={busy}
            error={panelError}
            onCancel={closePanel}
            onSubmit={(input) => void submitDeclaration(panel.location, input)}
          />
        )}

        {panel.kind === "spot" && selectedSpot !== null && (
          <SpotSheet
            spot={selectedSpot}
            busy={busy}
            error={panelError}
            onClose={closePanel}
            onReport={() => void submitReport(selectedSpot.id)}
            onRetract={() => void submitRetraction(selectedSpot.id)}
            onTakeDown={() => void submitTakeDown(selectedSpot.id)}
          />
        )}
      </div>
    </div>
  );
};
